<template>  
    <div>
        <EspMapControl :showMapTypeSwitch="showMapTypeSwitch" v-on:centerMap="centerMap()" v-on:changeMapType="changeMapType()" v-on:zoomMapOut="setZoomOnMap(-1)"  v-on:zoomMapIn="setZoomOnMap(1)"></EspMapControl>
        <HLLoadingMaskOverlay v-show="loading && showProgressCircular" class=""/>
        <div v-if="event" :id="mapName" class="google-map" :style="getStyle()" ></div>
    </div>
</template>

<script>
/* global google */
import LiveMap, {MAP_TYPE_DEFAULT_SATELLITE} from './liveMap.js'
import HLLoadingMaskOverlay from '@/components/ui/HLLoadingMaskOverlay.vue'
import {solutionMixin} from '@/utils/solution-mixin'
import {settingsMixin} from '@/utils/settings-mixin'
import {eventMixin} from '@/utils/event-mixin'
import EspMapControl from '@/components/map/EspMapControl.vue'
import CONFIG from '@/constants/config.js'
import { mapGetters } from 'vuex'

const LEVEL = 13;
const LEVEL_CREATE = 3;
const LAT_DEFAULT="0.0";
const LNG_DEFAULT="0.0";
//const EVENT_BUS_MAP_ZOOM_TO_PLAYER="mapZoomToPlayer";

export default {
    name: 'EspLiveMap',
    mixins:[solutionMixin, settingsMixin, eventMixin],
    components:{
        HLLoadingMaskOverlay,
        EspMapControl
    },
    props:{
        name: String,
        isLiveTracking: {
            type: Boolean,
            default: false
        },
        isHistory: {
            type: Boolean,
            default: false
        },
        teamsVisible: Array,
        zoom:{
            type:Number,
            default:null
        },
        checkinRadius:{
            type:Number,
            default:null
        },
        hideTiles: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        event:{},
        indoorMaps:{},
        eventMapstyle:{},
        teamPositions:{},
        eventWaypoints:{}
    },
    data() {
        return {
            mapName: this.name + "-map" + Date.now(),
            wpLat: '',
            wpLng: '',
            level: LEVEL_CREATE,
            city: '',
            trackingId: null,
            permissionStatus: null,
            checkinRadiusContent:this.checkinRadius,
            showProgressCircular: true,
        }
    },
    
    computed: {
        mapStyleData(){
            if(this.hideTiles && this.map){
                return this.map.getMapStyleInvisible();
            }
            // custom map styles only apply to the roadmap type; a satellite default wins (same as the native apps)
            return (this.settingsMapTypeDefault === MAP_TYPE_DEFAULT_SATELLITE)?null:this.eventMapstyle;
        },
        showMapTypeSwitch(){
            return !this.hideTiles;
        },
        ...mapGetters('spots', {
            getCurrentPosition : 'currentPosition'
        })

    },
    watch: {
        mapStyleData:{
            immediate:true,
            handler(val){
                if(val){
                    this.map.setMapStyle(val);
                }
            }
        },

        // the event usually arrives after the map was built, so re-apply the default type once it is known
        settingsMapTypeDefault(val){
            if(this.map){
                this.map.setDefaultMapType(val);
                if(this.mapStyleData){
                    this.map.setMapStyle(this.mapStyleData);
                }
            }
        },
       
        settingsGpsDisabled:{
            immediate:true,
            handler(val){ 
                if(!val){
                    this.tracking(true);
                }
                else{
                    this.tracking(false);
                }
            }
        },
       
        eventWaypoints(val,oldVal){
            if(this._.isEmpty(val) || !this.map){
                return;
            }
            if(!this._.isEqual(val,oldVal)){
                this.setNewEventwaypoints(val,this._.isEmpty(oldVal));
            }else{
                // same spot objects, mutated in place (route progress, hints, team sync): only icons may differ
                this.map.refreshWaypointIcons(val);
            }
        },
        teamsVisible(val){
            if(val){
                //All visable teams
                val.forEach(team => {
                    if(!this._.isEmpty(team)){    
                        this.setMapPlayersVisibility(team.id_team,true);
                    }
                });

                //All invisible teams
                if(this.teams){
                    let invisibleTeams = this._.differenceBy(this.teams, val);
                    if(this._.isArray(invisibleTeams)){
                        invisibleTeams.forEach(team=>{
                            this.setMapPlayersVisibility(team.id_team,false);
                        });
                    }
                }
            }
        },
        isHistory(val){
            if(val){
                this.map.enablePolylines();
            }else{
                this.map.disablePolylines();
            }
        }
    },
    methods:{

        onIsSpotSolved(spot){
            return this.spotIsSolved(spot);
        },
        onIsSpotFailed(spot){
            return this.spotIsFailed(spot);
        },
        onIsSpotInactive(spot){
            return this.spotIsInactive(spot);
        },

        centerSpot(spot){
            if(!this._.isEmpty(spot)){
                this.panToPosition(spot.map_lat,spot.map_lon);
            }
        },

        // a single spot changed state (solve, hint): swap its icon in place. A spot without a
        // marker is drawn by the eventWaypoints watcher once it shows up in the list.
        updateSpot(spot){
            if(this.map && !this._.isEmpty(spot)){
                this.map.refreshWaypointIcons([spot]);
            }
        },

        getDefaultCoords(){
            return {
                lat:Number(this.eventLat),
                lng:Number(this.eventLng)
            }
        },

        getStyle(){
            return "";
        },

        panToPosition(map_lat,map_lng){
            let latLng = new google.maps.LatLng(map_lat, map_lng);
            this.map.panToPosition(latLng.lat(), latLng.lng());
        },
        reload(){
            this.reloadEventwaypoints();
        },
        tracking(enabled) {
            if (enabled) {
                if(this.settingsEventNeedWebsocketConnection){
                    this.$websocket.connect(this.app_token, this.event.id_event);
                }
                this.getMyPosition();
                
                // Enable optional simulation helper on allowed environments
                if(this.isMapSimulationEnabled()){
                    this.test_setCustomLocationListener();
                }

            } else {
                this.stopTracking();
                this.setGpsStatus(CONFIG.GPS_STATUS_INACTIVE);
                this.$websocket.destroy();

            }
        },

        isMapSimulationEnabled(){
            const flag = process.env.VUE_APP_ENABLE_MAP_SIMULATION === 'true'
            const host = (typeof window !== 'undefined' && window.location)?window.location.hostname:"";
            const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
            return flag || isLocalhost;
        },

        // optional simulation helper
        // alt + click = set custom position
        // ctrl + click = enable position tracking
        test_setCustomLocationListener(){
            if(this.isMapSimulationEnabled() && this.map){
                this.map.getMap().addListener("click", (e) => {
                    console.log('map click event: ', e);
                    if(e.domEvent.altKey){
                        console.log('alt');
                        //stop tracking and set user position to clicked
                        this.stopTracking();
                        let position = {
                            coords: {
                                latitude: e.latLng.lat(),
                                longitude: e.latLng.lng(),
                                accuracy: 10,
                            }
                        };
                        this.showGpsPosition(position, false);
                        this.$websocket.broadcast.position(position);
                    }
                    else if(e.domEvent.ctrlKey || e.domEvent.metaKey){
                        console.log('ctrl');
                        //enable tracking
                        this.getMyPosition();
                    }
                });
            }
        },

        getMyPosition() {
            if (this.settingsGpsDisabled) {
                return;
            }
            if (navigator.geolocation) {
                // the immediate watcher and initMapSavety() both start tracking; drop the previous watch first
                this.stopTracking();
                this.setGpsStatus(CONFIG.GPS_STATUS_PENDING);
                var options = {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                };
                navigator.geolocation.getCurrentPosition(this.showCenterGpsPosition, this.showMapError);
                this.trackingId = navigator.geolocation.watchPosition(this.showGpsPosition, this.showMapError, options);
                this.watchPermissionState();
            } else {
                this.setGpsStatus(CONFIG.GPS_STATUS_UNAVAILABLE);
            }
        },
        stopTracking() {
            if (null !== this.trackingId) {
                navigator.geolocation.clearWatch(this.trackingId);
                this.trackingId = null;
            }
            this.$store.dispatch('geolocation/stopCoarseDetection');
        },
        setGpsStatus(status) {
            this.$store.commit('geolocation/SET_GPS_STATUS', status);
        },
        /**
         * Follows the browser's geolocation permission so a grant made in the site settings
         * restarts tracking without a reload. Best effort: browsers without the Permissions API
         * (or without 'geolocation' support in it) are left to the watchPosition error callback.
         */
        watchPermissionState() {
            if (this.permissionStatus || !navigator.permissions || !navigator.permissions.query) {
                return;
            }
            try {
                navigator.permissions.query({ name: 'geolocation' }).then((status) => {
                    this.permissionStatus = status;
                    if (status.state === 'denied') {
                        this.setGpsStatus(CONFIG.GPS_STATUS_DENIED);
                    }
                    status.addEventListener('change', this.onPermissionStateChange);
                }).catch(() => {});
            } catch (e) {
                // Safari < 16 throws synchronously for unknown permission names
            }
        },
        unwatchPermissionState() {
            if (this.permissionStatus) {
                this.permissionStatus.removeEventListener('change', this.onPermissionStateChange);
                this.permissionStatus = null;
            }
        },
        onPermissionStateChange() {
            if (!this.permissionStatus) {
                return;
            }
            if (this.permissionStatus.state === 'granted') {
                this.getMyPosition();
            } else if (this.permissionStatus.state === 'denied') {
                this.stopTracking();
                this.setGpsStatus(CONFIG.GPS_STATUS_DENIED);
            }
        },
        showCenterGpsPosition(position){
            this.showGpsPosition(position, true);
        },
        showGpsPosition(position, centerWithWaypoints = false) {
            if (position && position.coords) {
                this.setGpsStatus(CONFIG.GPS_STATUS_ACTIVE);
                this.$store.dispatch('geolocation/reportAccuracy', position.coords.accuracy);
                this.$websocket.broadcast.position(position);
                var latLng = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
                this.showPosition(latLng, this.level,true, position.coords.accuracy, centerWithWaypoints);
            } else {
                this.showMapError();
            }
        },
        showMapError(error) {
            const code = error ? error.code : undefined;
            if (error && code === error.PERMISSION_DENIED) {
                this.setGpsStatus(CONFIG.GPS_STATUS_DENIED);
            } else {
                // POSITION_UNAVAILABLE, TIMEOUT or a position without coords
                this.setGpsStatus(CONFIG.GPS_STATUS_UNAVAILABLE);
            }
            console.warn('geolocation error', code, error ? error.message : '');
        },

        showPosition(latLng, level, hideAnimation, accuracy, centerWithWaypoints) {
            if (null !== latLng) {
                this.level=level;

                this.map.setClickedPosition(latLng);

                this.map.showClickedPosition(true, null, hideAnimation, accuracy,this.checkinRadiusContent);
                if(centerWithWaypoints){
                    this.map.centerClickedPositionsAndWaypoints();
                }
                else{
                    // this.zoomToPosition(latLng);
                    // this.setLatLngToView(latLng);
                }
                // this.$store.commit('spots/SET_CURRENT_POSITION', {lat:latLng.lat(), lng:latLng.lng()})                
                this.$store.dispatch('spots/setCurrentPosition', { lat: latLng.lat(), lng: latLng.lng() });

                this.$emit('updateLocation',latLng);
            }
        },
        setRadiusToCurrentPosition(radius){
            if(this.map){
                this.checkinRadiusContent=radius;
                this.map.showClickedPosition(true, null, null, null,this.checkinRadiusContent);
            }
        },
        setLatLngToView(latLng){
            if(latLng && this.$refs.liveMapTable){
                this.$refs.liveMapTable.wpLat=latLng.lat().toFixed(6);
                this.$refs.liveMapTable.wpLng=latLng.lng().toFixed(6);
            }           
        },
        zoomToPosition(latLng){
             if(latLng && this.map && !this._.isEmpty(this.map.getMap())){
                this.map.getMap().setCenter(latLng); 
                var level = (this.map.getZoom() > LEVEL)? this.map.getZoom():this.level;
                this.map.zoomToPosition(latLng.lat(), latLng.lng(), level);
             }
        },

        initMapSavety(){
            if(!this.map){
                this.initMap();
            }
            if (this.mapStyleData) {
              this.map.setMapStyle(this.mapStyleData);
            }
            let latLng=this.map.getEventStartPosition();
            this.setLatLngToView(latLng);
            
            if(this.event){
                this.setNewEventwaypoints(this.eventWaypoints);    
                this.zoomToPosition(latLng);
                
                setTimeout(()=>{
                    if(this.eventWaypoints && this.eventWaypoints.length>0){
                        this.map.centerWaypointPositions();  
                    }
                          
                },100);
                     
            }
            if(!this.settingsGpsDisabled){                          
                this.tracking(true);
            }
        },
        destroyMap(){ 
            if(!this._.isEmpty(this.map)){
                this.map.destroy();
            }           
        },

        setNewEventwaypoints(newEventwaypoints,center=false){
            if(!this._.isEmpty(newEventwaypoints) && this.map){
                this.map.removeWaypoints();
                this.map.showWaypoints(newEventwaypoints,false);
                this.map.setIndoorMapsMode(this.indoorMaps,`/teamgeist/picture/indoormap/`,true,this.event.id_event);
                if(center){
                    //TODO zoom to position 
                    //this.zoomToPosition(latLng)
                    this.map.center();
                }
            }
        },
        initMap(){
            let map_lat = (this.event && !this._.isEmpty(this.event.map_lat))?this.event.map_lat:LAT_DEFAULT;
            let map_lng = (this.event && !this._.isEmpty(this.event.map_lng))?this.event.map_lng:LNG_DEFAULT;
            this.map = new LiveMap(this.mapName,map_lat,map_lng,this.level,this.settingsMapTypeDefault);
            this.map.setServerUrl(process.env.VUE_APP_BASE_URL);
            this.map.enableIconBubble(false);
            this.map.setSpotIsSolvedProofFct(this.onIsSpotSolved);
            this.map.setSpotIsFailedProofFct(this.onIsSpotFailed);
            this.map.setSpotIsInactiveProofFct(this.onIsSpotInactive);
            this.map.setClickedSpotPosition((m)=>{
                if(m && m.id){
                    this.$emit('select',m.id)
                }
            })

            if(this.isLiveTracking){
                this.loadTeamPositions();
            }
        },

        unregisterEventListener(){
            //EventBus.$off(EVENT_BUS_MAP_ZOOM_TO_PLAYER);         
        },
        registerEventListener(){
            /*
            if(this.isLiveTracking){                
                EventBus.$on(EVENT_BUS_MAP_ZOOM_TO_PLAYER, (data) =>{
                    if(this.checkTeamVisibility(data.id_team)){
                        if(this.map){
                            this.map.zoomToPlayer(data.id_team);
                        }
                    }
                });
            }
            */
        },
        checkTeamVisibility(id){
            if(this.teamsVisible){
                return this._.findIndex(this.teamsVisible, ['id_team', String(id)])>-1;
            }
            return false;
        },

        setMapPlayersVisibility(id_team, checked) {
            
            let id = String(id_team);
            if (typeof (id) === 'undefined' || null === id) {
                return;
            }
            if(this.map){
                id = parseInt(id);
                if (checked) {
                    if (this.isHistory) {
                        this.map.enablePolylines(id);
                    } else {
                        this.map.disablePolylines(id);
                    }
                    this.map.enablePlayer(id);
                } else {
                    this.map.disablePolylines(id);
                    this.map.disablePlayer(id);
                }
            }
        },
        removeLiveTrackingData(){

            if(this.map){
                this.map.removePolyLines();
                this.map.removePlayers();
            }
        },
        refreshTeam(id){
            if(this.map && id){
                this.map.removePlayer(id);
                //position callback ausführen mit letzter pos
            }
        },
        drawPlayer(id, name, lat, lng, alt, speed, bat, visible){
            setTimeout(()=>{
                
                //console.log("Position: " + id + " " + name + " " + lat + " " + lng+ " " +alt + " " + speed + " " + bat+ " " + visible);

                //Positionen auf Karte darstellen
                var color = this.$chat().getColorOfTeamId(id);
                var icon = this.$chat().getIconOfTeamId(id);
                
                if(this.checkTeamVisibility(id)){
                    this.map.showPlayer(id, name, lat, lng, alt, speed, bat, visible, color, icon, false);
                }else{
                    /*
                    context.map.showPlayer(id, name, lat, lng, alt, speed, bat, visible, color, icon, false); 
                    context.map.disablePolylines(id);
                    context.map.disablePlayer(id);
                    */
                }
                //context.addPlayerToMapOptions(id, name);
                //typ online setzen, wenn position received wird
            },0);
            
        },
        checkWhenToShowProgressCircular() {
            const mediaQuery = window.matchMedia('(max-width: 959px)')
            if (mediaQuery.matches) {
                this.showProgressCircular = false;
            }else{
                this.showProgressCircular = true;
            }
        },

        /**
         * Center the map -> if user location on user location else on event location
         */
        centerMap(){            
            //center (if gps is turned on, center map at user location. if gps is turned of, center map at event location)
            let getCurrentPosition = this.getCurrentPosition;
            if (getCurrentPosition && getCurrentPosition.lat && getCurrentPosition.lng){                
                this.panToPosition(getCurrentPosition.lat,getCurrentPosition.lng);
            }else{
                let latLng=this.map.getEventStartPosition();
                this.setLatLngToView(latLng);

                if (this.event){
                    this.setNewEventwaypoints(this.eventWaypoints);    
                    this.zoomToPosition(latLng);
                    
                    setTimeout(()=>{
                        if(this.eventWaypoints && this.eventWaypoints.length>0){
                            this.map.centerWaypointPositions();  
                        }
                            
                    },100);
                }
            }
        },
        
        /**
         * Change the map type stallite | road 
         */
        changeMapType(){            
            this.map.setMapType();
        },

        /**
         * Set the zoom on the map
         */
        setZoomOnMap(level){
            this.map.setZoomOnMap(level);
        }
        
    },
    mounted(){
        this.registerEventListener();
        if(this.isLiveTracking && !this._.isEmpty(this.$chat())){
            this.$chat().setPositionClb(this.drawPlayer);
        }
        this.initMapSavety();   
        this.checkWhenToShowProgressCircular();
        window.addEventListener('resize', this.checkWhenToShowProgressCircular);

    
    },
    beforeDestroy(){
        this.stopTracking();
        this.unwatchPermissionState();
        this.destroyMap();
        this.unregisterEventListener();
        window.removeEventListener('resize', this.checkWhenToShowProgressCircular); 
    }
}
</script>


<style scoped>
.google-map {
  width: 100%;
  position:absolute;
  top:0;
  right:0;
  bottom:0;
  left:0;
  background: gray;
}
</style>

<style>

.mapIndoorFloorSwitchBtn,.mapIndoorFloorSwitchInput{
    -moz-border-radius: 3px;
    border-radius: 3px;
    box-shadow: 0 1px 4px -1px rgba(0, 0, 0, 0.3);
    background-color:white;
    cursor: pointer;
    text-align: center;
    padding:5px;
}

.mapIndoorFloorSwitchBtn{
    width:50px;
}

.mapIndoorFloorSwitchBtn:hover{
    background-color: #EBEBEB;
}

.mapIndoorFloorSwitchInput{
    margin-top:5px;
    margin-bottom:5px;
    min-width: 50px;
}

.mapDropDownControl{
    direction: ltr; 
    overflow-x: hidden; 
    overflow-y: hidden; 
    text-align: left; 
    position: relative; 
    font-family: Arial, sans-serif; 
    -webkit-user-select: none; 
    font-size: 11px; 
    padding-top: 1px; 
    padding-right: 3px; 
    padding-bottom: 1px; 
    border-top-width: 1px; 
    border-right-width: 1px; 
    border-bottom-width: 1px; 
    border-left-width: 1px; 
    border-top-style: solid; 
    border-right-style: solid; 
    border-bottom-style: solid; 
    border-left-style: solid; 
    border-top-color: rgb(113, 123, 135); 
    border-right-color: rgb(113, 123, 135); 
    border-bottom-color: rgb(113, 123, 135); 
    border-left-color: rgb(113, 123, 135); 
    border-image: initial; 
    -webkit-box-shadow: rgba(0, 0, 0, 0.398438) 0px 2px 4px; 
    box-shadow: rgba(0, 0, 0, 0.398438) 0px 2px 4px; 
    color: rgb(0, 0, 0); 
    padding-left: 6px; 
    font-weight: bold; 
    background-image: initial; 
    background-attachment: initial; 
    background-origin: initial; 
    background-clip: initial; 
    background-color: rgb(255, 255, 255); 
    background-position: initial initial; 
    background-repeat: initial initial;     
    z-index: 2;
}   

.mapDropDownArrow{
    -webkit-user-select: none; 
    border-width: 0px; 
    border-style: initial; 
    border-color: initial; 
    border-image: initial; 
    padding: 0px; 
    margin-right: 0px; 
    margin-bottom: 0px; 
    margin-left: 0px; 
    position: absolute; 
    right: 6px; 
    top: 50%;
    margin-top: -2px; 
    width: 7px; 
    height: 4px; 
}

.mapDropDownOptionsDiv{
    background-color: white; 
    z-index: 0; 
    padding-top: 2px; 
    border-right-width: 1px; 
    border-bottom-width: 1px; 
    border-left-width: 1px; 
    border-right-style: solid; 
    border-bottom-style: solid; 
    border-left-style: solid; 
    border-right-color: rgb(113, 123, 135); 
    border-bottom-color: rgb(113, 123, 135); 
    border-left-color: rgb(113, 123, 135); 
    border-image: initial; 
    border-top-width: 0px; 
    border-top-style: initial; 
    border-top-color: initial; 
    -webkit-box-shadow: rgba(0, 0, 0, 0.398438) 0px 2px 4px; 
    box-shadow: rgba(0, 0, 0, 0.398438) 0px 2px 4px; 
    position: relative; 
    text-align: left; 
    display: none;
    max-height: 200px;
    overflow-x: hidden;
    overflow-y: scroll;
}

.mapDropDownItemDiv{
    font-family: Arial, sans-serif; 
    -webkit-user-select: none; 
    font-size: 13px; 
    padding-top: 2px; 
    padding-right: 5px; 
    padding-bottom: 3px; 
    padding-left: 5px; 
    background-color: rgb(255, 255, 255); 
    color: black;       
}
.mapDropDownItemDiv:hover, .checkboxContainer:hover{
    background-color: rgb(235, 235, 235);       

}
.mapDropDownControl:hover{
    background: -webkit-linear-gradient(top,rgb(255,255,255),rgb(230,230,230));
}
.mapSeparatorDiv{
    margin-top: 1px; 
    margin-right: 0px; 
    margin-bottom: 1px; 
    margin-left: 0px; 
    border-top-width: 1px; 
    border-top-style: solid; 
    border-top-color: rgb(235, 235, 235); 
}
.maploadingMaskDiv{
    height:20px;
    padding:5px;
}
.mapCheckboxContainer{
    font-family: Arial, sans-serif; 
    -webkit-user-select: none; 
    font-size: 11px; 
    padding-top: 1px; 
    padding-bottom: 3px; 
    padding-left: 5px; 
    direction: ltr; 
    text-align: left; 
    background-color: rgb(255, 255, 255); 
    white-space: nowrap; 
    padding-right: 8px; 
    color: rgb(0, 0, 0); 
}
.mapCheckboxIsSelected{
    background:#CCCCCC;
}
.mapCheckboxSpan{
    box-sizing: border-box; 
    position: relative; 
    line-height: 0; 
    font-size: 0px; 
    margin-top: 0px; 
    margin-right: 5px; 
    margin-bottom: 0px; 
    margin-left: 0px; 
    display: inline-block; 
    background-color: rgb(255, 255, 255); 
    border-width: 1px; 
    border-style: solid; 
    border-color: initial; 
    border-image: initial; 
    border-top-left-radius: 1px; 
    border-top-right-radius: 1px; 
    border-bottom-right-radius: 1px; 
    border-bottom-left-radius: 1px; 
    width: 13px; 
    height: 13px; 
    vertical-align: middle; 
    -webkit-box-shadow: none; 
    box-shadow: none; 
    border-color: rgb(198, 198, 198);       
}


.blankDiv{
    position: absolute; 
    left: 1px; 
    top: -2px; 
    width: 13px; 
    height: 11px; 
    overflow-x: hidden; 
    overflow-y: hidden; 
    display: none; /*when = block -> this is the "check" symbol*/ 
}

.mapBlankImg{
    position: absolute; 
    left: -52px; 
    top: -44px; 
    -webkit-user-select: none; 
    border-width: 0px; 
    border-style: initial; 
    border-color: initial; 
    border-image: initial; 
    padding: 0px; 
    margin: 0px; 
    -webkit-user-drag: none; 
    width: 68px; 
    height: 67px; 
}

.mapBlankDiv {
    display: none;
    height: 11px;
    left: 1px;
    overflow: hidden;
    position: absolute;
    top: -2px;
    width: 13px;
}

.mapCheckboxLabel{
    vertical-align: middle; 
    cursor: pointer;
}

.mapBox label,#map_canvas label{
    float:none!important; 
}
</style>
